import { PageContainer, PageHeader } from '@/components/Atoms'
import { Block } from '@/components/Block'
import { ExportCsvButton } from '@/components/ExportCsv/ExportCsvButton'
import PermissionsGuard from '@/components/Guard/PermissionsGuard'
import { PendingChanges } from '@/components/PendingChanges'
import {
  ConsumerRates,
  consumerRatesExportQueryOptions,
  useConsumerRatesList,
  useUpdateConsumerRates,
} from '@/hooks/queries/crt/useConsumerRates'
import { useLastUpdatedConsumerRatesLog } from '@/hooks/queries/useAuditTrails'
import { formatLastUpdatedAssumptionLog } from '@/lib/utils/crt'
import { queryClient } from '@/main'
import { UpdateConsumerRates } from '@repo/backend/crt'
import { createFileRoute } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ConsumerRatesDataTable from './-components/ConsumerRatesDataTable'
import { UploadConsumerRatesDialog } from './-components/UploadConsumerRatesDialog'

export const Route = createFileRoute('/_auth/_modules/crt/pricing-excise/insights/consumer-rates/')(
  {
    component: RouteComponent,
  },
)

type EditedValue = {
  gst?: string
  pst?: string
  hst?: string
  qst?: string
}

function RouteComponent() {
  const { t } = useTranslation('crt')

  const [editedValues, setEditedValues] = useState<Map<number, EditedValue>>(new Map())

  const { data, isPending, error } = useConsumerRatesList()
  const { data: lastUpdatedConsumerRatesTrail } = useLastUpdatedConsumerRatesLog()

  const lastUpdatedLog = formatLastUpdatedAssumptionLog(lastUpdatedConsumerRatesTrail)

  const { mutate: updateRates, isPending: isSaving } = useUpdateConsumerRates({
    handleOnSuccess: () => {
      setEditedValues(new Map())
    },
  })

  const originalById = new Map((data?.results ?? []).map((row) => [row.id, row]))

  const handleValueChange = useCallback(
    (id: number, field: keyof EditedValue, value: string) => {
      const sanitized = !value || Number(value) < 0 ? '0' : value

      setEditedValues((prev) => {
        const newMap = new Map(prev)
        const existing = newMap.get(id) ?? {}
        const updated = { ...existing, [field]: sanitized }

        const original = originalById.get(id)
        if (original) {
          const allMatch = Object.entries(updated).every(
            ([k, v]) => v === (original[k as keyof EditedValue] ?? '0'),
          )
          if (allMatch) {
            newMap.delete(id)
            return newMap
          }
        }

        newMap.set(id, updated)
        return newMap
      })
    },
    [originalById],
  )

  const getDisplayValue = useCallback(
    (row: ConsumerRates, field: keyof EditedValue): string => {
      const edited = editedValues.get(row.id)
      if (edited && edited[field] !== undefined) {
        return edited[field] ?? ''
      }
      return row[field] ?? '0'
    },
    [editedValues],
  )

  const handleSaveChanges = () => {
    if (editedValues.size === 0) return

    const updates: UpdateConsumerRates['updates'] = []

    editedValues.forEach((values, id) => {
      updates.push({
        id,
        ...values,
      })
    })

    updateRates({ updates })
  }

  const areChangesPending = editedValues.size > 0

  const getCsvExport = () => queryClient.ensureQueryData(consumerRatesExportQueryOptions())

  return (
    <PageContainer className="pb-28">
      <Block shouldBlock={areChangesPending} />

      <PageHeader>{t('consumerRates.pageHeader')}</PageHeader>

      <ConsumerRatesDataTable
        data={data?.results ?? []}
        isPending={isPending}
        error={error}
        editedValues={editedValues}
        toolbarActions={
          <>
            <ExportCsvButton
              getCsvExport={getCsvExport}
              rowCount={data?.results.length ?? 0}
              label={t('export.viewData', { ns: 'common' })}
            />
            <UploadConsumerRatesDialog />
          </>
        }
        getDisplayValue={getDisplayValue}
        onValueChange={handleValueChange}
      />

      <PermissionsGuard required={['crt_consumer_rates_edit']}>
        <PendingChanges
          areChangesPending={areChangesPending}
          lastUpdatedLog={lastUpdatedLog}
          isSavePending={isSaving}
          onSaveChanges={handleSaveChanges}
        />
      </PermissionsGuard>
    </PageContainer>
  )
}
